// WHICH DRAFT A MEMBER RESUMES, AND WHY IT IS NOT A COIN TOSS.
//
// Until 2026-08-23 NewClaim adopted a draft with `limit(1)` and NO `orderBy`. Firestore returns
// whichever document it likes, so a member with more than one draft got an ARBITRARY one bound to
// the form, and the next save wrote over it with no prompt. That is the data-loss case behind the
// whole intake rebuild, recorded as the second defect in data\YCP_INTAKE_INSPECTION_2026-08-22.md
// and re-verified at the 2026-08-23 muster.
//
// Two things had to be true for the fix to be safe:
//
//   1. NO NEW COMPOSITE INDEX. Adding orderBy('updatedAt') to a query that already carries two
//      equality filters would require a composite index in firestore.indexes.json, and
//      ycp.cmd has no step that deploys indexes. Until that index existed in the project the query
//      would throw at runtime, and the caller's `.catch(() => {})` would swallow it - so draft
//      adoption would silently stop working and nothing would say so. Equality-only queries are
//      served from single-field indexes, so the query keeps its two `where` clauses, drops the
//      `limit`, and the ORDER IS DECIDED HERE, in code that can be tested without Firestore.
//
//   2. NEWEST WINS, AND THE MEMBER IS TOLD WHEN THERE ARE OTHERS. Silently binding the newest is
//      still silent. The caller gets the count so the screen can say so.

import { FORM_FIELDS } from './claimPayload';

// Firestore Timestamps compare by seconds then nanoseconds. A document mid-write can carry a null
// for a serverTimestamp() field that has not resolved yet, so every accessor tolerates absence
// rather than throwing on it.
function stamp(v) {
  if (!v) return 0;
  if (typeof v.seconds === 'number') return v.seconds * 1e9 + (v.nanoseconds || 0);
  if (typeof v.toMillis === 'function') return v.toMillis() * 1e6;
  if (v instanceof Date) return v.getTime() * 1e6;
  return 0;
}

// A draft's recency is when it was last SAVED, not when it was created. Falls back to createdAt so
// a draft written before updatedAt existed still sorts sensibly rather than to the bottom.
export function draftRecency(d) {
  const data = (d && typeof d.data === 'function' ? d.data() : d) || {};
  return stamp(data.updatedAt) || stamp(data.createdAt) || 0;
}

// Newest first. STABLE on ties: two drafts saved in the same second keep their input order rather
// than swapping between renders, which would make the resumed draft change under the member.
export function sortDraftsNewestFirst(docs) {
  return (docs || [])
    .map((d, i) => ({ d, i, r: draftRecency(d) }))
    .sort((a, b) => (b.r - a.r) || (a.i - b.i))
    .map(x => x.d);
}

/**
 * Decide what to resume.
 * @param {Array} docs  the member's draft documents, in any order
 * @returns {{ draft: *|null, others: number, total: number }}
 *          `draft`  the one to bind - the most recently saved, or null if there are none
 *          `others` how many the member has BESIDES the one being resumed. Non-zero means the
 *                   screen must say so; the whole defect was that it did not.
 */
export function pickResumableDraft(docs) {
  const sorted = sortDraftsNewestFirst(docs);
  return {
    draft: sorted.length ? sorted[0] : null,
    others: Math.max(0, sorted.length - 1),
    total: sorted.length,
  };
}

// RESUMING A DRAFT WITHOUT RE-OPENING THE WRITE PATH THE BUILDER CLOSED.
//
// Until 2026-08-23 both intake screens resumed a draft with `setForm(prev => ({ ...prev, ...data }))`.
// That is every key the stored document happens to carry, poured back into form state - the second
// half of defect D-2, and the half that survived the write-path unification.
//
// It matters because `buildClaimPayload` resolves `scopeKey`, `routingStatus`, `committee` and
// `localCommittee` FRESH on every write, specifically so a caller's bag cannot pin a stale route.
// The unbounded merge handed the form a route from whenever the draft was last saved, and the
// builder then read `craft`, `railroad` and `workLocation` back out of a bag that had been
// contaminated by it. It also re-admitted DROPPED fields - `violationType`, `rulesViolated`,
// `yardOfIncident` - which the ruling of 2026-08-22 retired, so a resumed draft could quietly
// resurrect a representation the record says is gone.
//
// The fix is not to filter the DROPPED list out. That is a denylist, and a denylist is wrong for the
// usual reason: it must be updated every time the schema grows, and it fails silently when it is
// not. `FORM_FIELDS` is an ALLOW-LIST built from the very arrays the builder writes, so a field
// added to the payload becomes a field the form can resume, automatically and without an edit here.

/**
 * The subset of a stored draft that may go back into form state.
 * @param {object} data the stored claim document
 * @returns {object} only the keys the form owns
 */
export function draftIntoForm(data) {
  const out = {};
  if (!data || typeof data !== 'object') return out;
  for (const k of FORM_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(data, k)) out[k] = data[k];
  }
  return out;
}

// Which keys the merge refused. Not used to decide anything - used so a caller can log it, and so
// the test can assert on WHAT was excluded rather than only on what survived. A resumed draft that
// drops `scopeKey` is the fix working; one that drops `incidentDate` would be the fix broken.
export function droppedDraftKeys(data) {
  if (!data || typeof data !== 'object') return [];
  return Object.keys(data).filter(k => !FORM_FIELDS.includes(k));
}

// What the screen says when a member has more than one draft. Kept here rather than in JSX so the
// wording is testable and so it cannot drift between the two intake screens.
export function multipleDraftsNotice(others) {
  if (!others || others < 1) return '';
  return others === 1
    ? 'You have 1 other saved draft. This is your most recent one - the other is untouched.'
    : `You have ${others} other saved drafts. This is your most recent one - the others are untouched.`;
}
